import EmailInput from '@/components/app-ui/form/EmailInput';
import SubmitButton from '@/components/app-ui/form/SubmitButton';
import TextLink from '@/components/app-ui/TextLink';
import { Button } from '@/components/ui/button';
import { emailResolver } from '@/constants/form.constants';
import IMAGE_CONSTANTS from '@/constants/image.constants';
import { HOME_ROUTE } from '@/constants/ui.constants';
import userApi from '@/services/user-api.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { signIn } from 'next-auth/react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const schema = z.object({ email: emailResolver() });

type SignupLandingProps = {
	setSignupEmail: (value: string) => void;
	setOnFormScreen: (value: boolean) => void;
};

export default function SignupLanding({ setSignupEmail, setOnFormScreen }: SignupLandingProps) {
	const searchParams = useSearchParams();
	const callbackUrl = searchParams.get('callbackUrl') || HOME_ROUTE;

	const preSignupForm = useForm<{ email: string }>({
		defaultValues: {
			email: '',
		},
		mode: 'onSubmit',
		resolver: zodResolver(schema),
	});
	const { handleSubmit, formState, control, setError } = preSignupForm;
	const { errors } = formState;
	const [isLoading, setIsLoading] = useState(false);

	const submitForm = async (data: { email: string }) => {
		setIsLoading(true);
		const userExists = await userApi.checkIfUserExists(data.email);
		setIsLoading(false);
		if (userExists) return setError('email', { message: 'Email exists, please login instead' });
		setSignupEmail(data.email);
		setOnFormScreen(true);
	};

	return (
		<>
			<Button
				onClick={() => signIn('google', { callbackUrl })}
				variant="outline"
				size="default"
				className="w-full"
			>
				<Image src={IMAGE_CONSTANTS.SSO.GOOGLE_LOGO} alt="Sign up with Google" width={20} height={20} />
				<span className="ml-2 text-sm">Sign up with Google</span>
			</Button>

			<Button
				onClick={() => signIn('azure-ad', { callbackUrl })}
				variant="outline"
				size="default"
				className="mt-4 w-full"
			>
				<Image src={IMAGE_CONSTANTS.SSO.MICROSOFT_LOGO} alt="Sign up with Microsoft" width={20} height={20} />
				<span className="ml-2 text-sm">Sign up with Microsoft</span>
			</Button>

			<div>
				<div className="relative mt-4">
					<div className="absolute inset-0 flex items-center" aria-hidden="true">
						<div className="w-full border-t border-subtle" />
					</div>
					<div className="relative flex justify-center text-sm font-medium leading-6">
						<span className="bg-background px-6 text-foreground">Or</span>
					</div>
				</div>
				<form noValidate onSubmit={handleSubmit(submitForm)} className="flex flex-col gap-2">
					<EmailInput
						name="email"
						placeholder="Email"
						control={control}
						errorMessage={errors.email?.message}
					/>
					<div className="mt-3">
						<SubmitButton isSubmitting={isLoading} fullWidth>
							Sign up with email
						</SubmitButton>
					</div>
				</form>
			</div>
			<p className="app-text-muted mt-6 text-center">
				{`Already a user? `}
				<TextLink href="/auth/login">Login now</TextLink>
			</p>
		</>
	);
}
